import {
  Button, Card, Input, InputNumber, Modal, Select, Space, Table, Typography,
} from 'antd';
import rentalApi from 'api/rentalApi';
import ToastHelper from 'general/helpers/ToastHelper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DEPOSIT_STATUS_OPTIONS, DepositStatusTag, formatDateTime, formatMoney, getRentalErrorMessage,
} from '../../rentalConstants';

const { Text, Title } = Typography;

function RentalDepositQueueScreen() {
  const [status, setStatus] = useState('PENDING_CONFIRMATION');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [confirmModal, setConfirmModal] = useState(null);
  const [externalTransactionId, setExternalTransactionId] = useState('');
  const [receiptMediaId, setReceiptMediaId] = useState(null);

  const [rejectModal, setRejectModal] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rentalApi.getDepositTransactions({ status: status || undefined, page, pageSize });
      setRows(response?.depositTransactions || []);
      setTotal(response?.total || 0);
    } catch (error) {
      setRows([]);
      setTotal(0);
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [status, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const submitConfirm = async () => {
    setActionLoading(true);
    try {
      await rentalApi.confirmDeposit(confirmModal.rentalDepositTransactionId, {
        externalTransactionId: externalTransactionId.trim() || undefined,
        receiptMediaId: receiptMediaId || undefined,
      });
      ToastHelper.showSuccess('Đã xác nhận đặt cọc, xe chuyển sang trạng thái hoạt động');
      setConfirmModal(null);
      setExternalTransactionId('');
      setReceiptMediaId(null);
      load();
    } catch (error) {
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setActionLoading(false);
    }
  };

  const submitReject = async () => {
    if (!reason.trim()) return;
    setActionLoading(true);
    try {
      await rentalApi.rejectDeposit(rejectModal.rentalDepositTransactionId, reason.trim());
      ToastHelper.showSuccess('Đã từ chối giao dịch đặt cọc');
      setRejectModal(null);
      setReason('');
      load();
    } catch (error) {
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setActionLoading(false);
    }
  };

  const columns = useMemo(() => [
    { title: 'Mã GD', dataIndex: 'rentalDepositTransactionId', width: 90, render: (value) => <Text strong>#{value}</Text> },
    {
      title: 'Xe',
      key: 'vehicle',
      render: (_, row) => (
        <div>
          <div><Text strong>{row.vehicle?.vehicleCode}</Text></div>
          <Text type="secondary">{row.vehicle?.plate} · {row.vehicle?.model}</Text>
        </div>
      ),
    },
    { title: 'Chiều', dataIndex: 'direction', width: 100 },
    { title: 'Số tiền', dataIndex: 'amount', width: 140, render: formatMoney },
    { title: 'Trạng thái', dataIndex: 'status', width: 150, render: (value) => <DepositStatusTag status={value} /> },
    { title: 'Tạo lúc', dataIndex: 'createdAt', width: 170, render: formatDateTime },
    {
      title: '',
      key: 'action',
      width: 220,
      render: (_, row) => (row.status === 'PENDING_CONFIRMATION' ? (
        <Space>
          <Button type="primary" size="small" onClick={() => setConfirmModal(row)}>Xác nhận</Button>
          <Button danger size="small" onClick={() => setRejectModal(row)}>Từ chối</Button>
        </Space>
      ) : null),
    },
  ], []);

  return (
    <div className="container-fluid px-4 my-4">
      <Card>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <Title level={4} className="mb-0">Đặt cọc xe cho thuê</Title>
            <Text type="secondary">Tổng {total} giao dịch theo bộ lọc</Text>
          </div>
          <Button onClick={load} loading={loading}>Làm mới</Button>
        </div>
        <Space wrap className="mb-4">
          <Select
            value={status}
            options={DEPOSIT_STATUS_OPTIONS}
            style={{ width: 190 }}
            onChange={(value) => { setStatus(value); setPage(1); }}
          />
        </Space>
        <Table
          rowKey="rentalDepositTransactionId"
          loading={loading}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 900 }}
          locale={{ emptyText: 'Không có dữ liệu' }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => { setPage(nextPage); setPageSize(nextPageSize); },
          }}
        />
      </Card>

      <Modal
        title={`Xác nhận đặt cọc #${confirmModal?.rentalDepositTransactionId || ''}`}
        open={Boolean(confirmModal)}
        okText="Xác nhận đã nhận cọc"
        cancelText="Huỷ"
        confirmLoading={actionLoading}
        onCancel={() => { setConfirmModal(null); setExternalTransactionId(''); setReceiptMediaId(null); }}
        onOk={submitConfirm}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Số tiền cọc: <Text strong>{formatMoney(confirmModal?.amount)}</Text></Text>
          <Input
            placeholder="Mã giao dịch chuyển khoản (tuỳ chọn)"
            value={externalTransactionId}
            onChange={(event) => setExternalTransactionId(event.target.value)}
          />
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Media ID ảnh biên lai (tuỳ chọn)"
            value={receiptMediaId}
            onChange={setReceiptMediaId}
            min={1}
          />
        </Space>
      </Modal>

      <Modal
        title={`Từ chối đặt cọc #${rejectModal?.rentalDepositTransactionId || ''}`}
        open={Boolean(rejectModal)}
        okText="Xác nhận từ chối"
        cancelText="Huỷ"
        confirmLoading={actionLoading}
        okButtonProps={{ danger: true, disabled: !reason.trim() }}
        onCancel={() => { setRejectModal(null); setReason(''); }}
        onOk={submitReject}
      >
        <Input.TextArea
          rows={4}
          maxLength={1000}
          showCount
          value={reason}
          placeholder="Nêu rõ lý do từ chối để đối tác nộp lại"
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>
    </div>
  );
}

export default RentalDepositQueueScreen;
